import { ChangeDetectionStrategy, Component, computed, inject, PipeTransform, signal, TemplateRef, viewChild } from '@angular/core';
import { Observable, of } from 'rxjs';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTooltip } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { SnackbarService, FeedbackInfoComponent } from 'ok-angular-shared/components';
import { ChargingStationLocationService } from 'src/app/shared/services/charging-station-location.service';
import { TypeAheadSelector2Component } from 'src/app/shared/components/type-ahead-selector2/type-ahead-selector2.component';
import { AdminEnumSelectListMultipleComponent } from 'src/app/shared/components/admin-enum-select-list-multiple/admin-enum-select-list-multiple.component';
import {
  AssignedPriceRegulation,
  AvailablePriceRegulationResponse,
  AvailablePriceRegulations,
  LocationPriceRegulation,
  ConnectPriceRegulationForm,
  ConnectPriceRegulationModalData,
  PriceRegulationAccessGroup,
  PriceRegulationReference,
} from '../charging-station-location-price-regulation.model';
import { TranslateAccessGroupCarTypeEnum } from '../../../shared/pipes/translate-access-group-car-type-enum.pipe';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorMessage } from 'src/app/shared/models/errorMessage.model';
import { DkCurrencyPipe } from '../../../shared/pipes/dk-currency';

@Component({
  selector: 'adm-price-regulation-dialog',
  templateUrl: './connect-price-regulation-dialog.component.html',
  styleUrl: './connect-price-regulation-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatDialogModule,
    MatIconModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatTooltip,
    FeedbackInfoComponent,
    RouterLink,
    TypeAheadSelector2Component,
    AdminEnumSelectListMultipleComponent,
    DkCurrencyPipe,
    TranslateAccessGroupCarTypeEnum,
  ],
})
export class ConnectPriceRegulationDialogComponent {
  private snackbarService = inject(SnackbarService);
  private chargingStationLocationService = inject(ChargingStationLocationService);

  data = inject<ConnectPriceRegulationModalData>(MAT_DIALOG_DATA);

  priceRegulations = signal<LocationPriceRegulation[]>([]);
  accessGroups = signal<PriceRegulationAccessGroup[]>([]);
  availablePriceRegulations = signal<AvailablePriceRegulations>([]);
  editingPriceRegulation = signal<PriceRegulationReference | null>(null);
  lastSavedPriceRegulationId = signal<string | null>(null);
  selectedAccessGroupIds = signal<PriceRegulationAccessGroup[]>([]);
  typeAheadResetTrigger = signal({ reset: false });
  private selectedPriceRegulationId = signal<string>('');
  private selectedAccessGroupIdentifiers = signal<string[]>([]);

  canSubmit = computed(() => this.selectedPriceRegulationId().length > 0 && this.selectedAccessGroupIdentifiers().length > 0);
  // Distinct name avoids shadowing the local #priceRegulationItemTemplate template variable
  priceRegulationTemplate = viewChild<TemplateRef<unknown>>('priceRegulationItemTemplate');
  accessGroupTemplate = viewChild<TemplateRef<unknown>>('accessGroupItemTemplate');

  accessGroupTranslatePipe = computed<PipeTransform>(() => ({
    transform: (ag: PriceRegulationAccessGroup) => ag.name,
  }));

  formGroup = new FormGroup<ConnectPriceRegulationForm>({
    priceRegulationIdentifier: new FormControl<string>('', { nonNullable: true }),
    accessGroupIdentifiers: new FormControl<string[]>([], { nonNullable: true }),
  });

  fetchPriceRegulationsFn = (term: string): Observable<AvailablePriceRegulations> => {
    const lower = term.toLowerCase();
    return of(this.uniqueAvailablePriceRegulations().filter(r => r.name.toLowerCase().includes(lower)));
  };

  onPriceRegulationSelected(regulation: AvailablePriceRegulationResponse | null): void {
    this.formGroup.controls.priceRegulationIdentifier.setValue(regulation?.identifier ?? '');
    this.selectedPriceRegulationId.set(regulation?.identifier ?? '');
  }

  constructor() {
    this.chargingStationLocationService.getPriceRegulationByIdentifier(this.data.locationIdentifier).subscribe({
      next: response => this.priceRegulations.set(response.price_regulations ?? []),
    });

    this.chargingStationLocationService.getPriceRegulationAccessGroups(this.data.locationIdentifier).subscribe({
      next: response => this.accessGroups.set(response.access_groups ?? []),
      error: () => this.snackbarService.error('Der opstod en fejl ved indlæsning af adgangsgrupper for lokationen'),
    });

    this.chargingStationLocationService.getAvailablePriceRegulations(this.data.locationIdentifier).subscribe({
      next: response => this.availablePriceRegulations.set(response ?? []),
      error: () => this.snackbarService.error('Der opstod en fejl ved indlæsning af tilgængelige prisreguleringer'),
    });
  }

  create(): void {
    if (!this.canSubmit()) return;
    const { priceRegulationIdentifier, accessGroupIdentifiers } = this.formGroup.getRawValue();

    this.chargingStationLocationService
      .assignPriceRegulationToLocation(this.data.locationIdentifier, priceRegulationIdentifier, accessGroupIdentifiers)
      .subscribe({
        next: () => {
          this.snackbarService.success('Prisreguleringen er blevet tilknyttet lokationen');
          this.resetForm();
          this.refreshPriceRegulations();
          this.refreshAvailablePriceRegulations();
          this.refreshAccessGroups();
        },
        error: (httpError: HttpErrorResponse) => {
          const error = httpError.error as ErrorMessage;
          switch (error.code) {
            // 404 errors
            case 13001:
              this.snackbarService.error('Denne adgangsgruppe blev ikke fundet');
              break;
            case 30004:
              this.snackbarService.error('Denne lokation blev ikke fundet');
              break;
            case 906002:
              this.snackbarService.error('Denne prisregulering blev ikke fundet');
              break;
            // 409 errors
            case 906011:
              this.snackbarService.error('Denne adgangsgruppe tilhører ikke organisationen, der ejer lokationen');
              break;
            case 906012:
              this.snackbarService.error('Denne adgangsgruppe er allerede tilknyttet prisreguleringen på lokationen');
              break;
            case 906014:
              this.snackbarService.error('Denne prisregulering matcher ikke den forventede type for denne lokation');
              break;
            case 906015:
              this.snackbarService.error('Denne prisregulering er allerede tilknyttet lokationen');
              break;
          }
        },
      });
  }

  onAccessGroupsChanged(accessGroups: PriceRegulationAccessGroup[] | null): void {
    const identifiers = accessGroups?.map(ag => ag.identifier) ?? [];
    this.formGroup.controls.accessGroupIdentifiers.setValue(identifiers);
    this.selectedAccessGroupIdentifiers.set(identifiers);
  }

  edit(priceRegulation: PriceRegulationReference): void {
    this.lastSavedPriceRegulationId.set(null);
    const assignedAccessGroups = this.accessGroupsFor(priceRegulation.name);
    const ids = assignedAccessGroups.map(ag => ag.identifier);
    const availableAccessGroups = this.accessGroups();
    // The groups taken by this row are not returned as available, so merge them in to keep them selectable while editing.
    const mergedAccessGroups = [
      ...availableAccessGroups,
      ...assignedAccessGroups.filter(assigned => !availableAccessGroups.some(available => available.identifier === assigned.identifier)),
    ];

    this.accessGroups.set(mergedAccessGroups);
    this.editingPriceRegulation.set(priceRegulation);
    this.selectedAccessGroupIds.set(mergedAccessGroups.filter(ag => ids.includes(ag.identifier)));
    this.formGroup.patchValue({
      priceRegulationIdentifier: priceRegulation.identifier,
      accessGroupIdentifiers: ids,
    });
    this.selectedPriceRegulationId.set(priceRegulation.identifier);
    this.selectedAccessGroupIdentifiers.set(ids);
  }

  delete(priceRegulation: PriceRegulationReference): void {
    this.chargingStationLocationService.deletePriceRegulationRelation(this.data.locationIdentifier, priceRegulation.identifier).subscribe({
      next: () => {
        this.snackbarService.success(`Prisreguleringen '${priceRegulation.name}' er blevet fjernet fra lokationen`);
        this.refreshPriceRegulations();
        this.refreshAvailablePriceRegulations();
        this.refreshAccessGroups();
      },
      error: (httpError: HttpErrorResponse) => {
        const error = httpError.error as ErrorMessage;
        switch (error.code) {
          // 404 errors
          case 906016:
            this.snackbarService.error('Denne prisregulering er ikke tilknyttet lokationen');
            break;
        }
      },
    });
  }

  saveEdit(): void {
    if (!this.canSubmit() || !this.editingPriceRegulation()) return;
    const { priceRegulationIdentifier, accessGroupIdentifiers } = this.formGroup.getRawValue();

    this.chargingStationLocationService
      .editAccessGroups(this.data.locationIdentifier, priceRegulationIdentifier, accessGroupIdentifiers)
      .subscribe({
        next: () => {
          this.snackbarService.success('Prisreguleringen er blevet opdateret');
          this.lastSavedPriceRegulationId.set(priceRegulationIdentifier);
          setTimeout(() => this.lastSavedPriceRegulationId.set(null), 3000);
          this.editingPriceRegulation.set(null);
          this.resetForm();
          this.refreshPriceRegulations();
          this.refreshAccessGroups();
        },
        error: (httpError: HttpErrorResponse) => {
          const error = httpError.error as ErrorMessage;
          switch (error.code) {
            // 404 errors
            case 13001:
              this.snackbarService.error('Denne adgangsgruppe blev ikke fundet');
              break;
            case 906016:
              this.snackbarService.error('Denne prisregulering er ikke tilknyttet lokationen');
              break;

            // 409 errors
            case 906011:
              this.snackbarService.error('Denne prisregulering tilhører ikke den samme organisation som lokationen');
              break;
            case 906012:
              this.snackbarService.error('Denne adgangsgruppe er allerede tilknyttet prisreguleringen på lokationen');
              break;
          }
        },
      });
  }

  cancelEdit(): void {
    this.lastSavedPriceRegulationId.set(null);
    this.editingPriceRegulation.set(null);
    this.resetForm();
    this.refreshAccessGroups();
  }

  // Access groups tied to the location's price regulation with the given name, deduplicated by identifier.
  accessGroupsFor(priceRegulationName: string): PriceRegulationAccessGroup[] {
    const seen = new Set<string>();
    return this.priceRegulations()
      .filter(priceRegulation => priceRegulation.price_regulation.name === priceRegulationName)
      .map(priceRegulation => priceRegulation.access_group)
      .filter(accessGroup => (seen.has(accessGroup.identifier) ? false : (seen.add(accessGroup.identifier), true)));
  }

  // Price regulations assigned to the location, deduplicated by identifier, with type from version info.
  uniquePriceRegulations = computed(() => {
    const seen = new Set<string>();
    return this.priceRegulations()
      .filter(pr => (seen.has(pr.price_regulation.identifier) ? false : (seen.add(pr.price_regulation.identifier), true)))
      .map((pr): AssignedPriceRegulation => ({
        ref: pr.price_regulation,
        versionId: pr.price_regulation_version.identifier,
        name: pr.price_regulation.name,
        type: pr.price_regulation_version.price_regulation_type,
        okSupplement: pr.price_regulation_version.ok_supplement_override_in_kr_without_vat ?? null,
        orgSupplement: pr.price_regulation_version.organization_supplement_override_in_kr_without_vat ?? null,
        discount: pr.price_regulation_version.discount_amount_in_kr_without_vat,
      }));
  });

  // Unique price regulations available to assign, deduplicated by identifier.
  uniqueAvailablePriceRegulations = computed(() => {
    const seen = new Set<string>();
    return this.availablePriceRegulations().filter(item => (seen.has(item.identifier) ? false : (seen.add(item.identifier), true)));
  });

  private resetForm(): void {
    this.selectedAccessGroupIds.set([]);
    this.selectedPriceRegulationId.set('');
    this.selectedAccessGroupIdentifiers.set([]);
    this.typeAheadResetTrigger.set({ reset: true });
    this.formGroup.reset();
    this.formGroup.markAsPristine();
    this.formGroup.markAsUntouched();
  }

  private refreshPriceRegulations(): void {
    this.chargingStationLocationService.getPriceRegulationByIdentifier(this.data.locationIdentifier).subscribe({
      next: response => this.priceRegulations.set(response.price_regulations ?? []),
      error: () => this.snackbarService.error('Der opstod en fejl ved genindlæsning af prisreguleringer'),
    });
  }

  private refreshAvailablePriceRegulations(): void {
    this.chargingStationLocationService.getAvailablePriceRegulations(this.data.locationIdentifier).subscribe({
      next: response => this.availablePriceRegulations.set(response ?? []),
      error: () => this.snackbarService.error('Der opstod en fejl ved genindlæsning af tilgængelige prisreguleringer'),
    });
  }

  private refreshAccessGroups(): void {
    this.chargingStationLocationService.getPriceRegulationAccessGroups(this.data.locationIdentifier).subscribe({
      next: response => this.accessGroups.set(response.access_groups ?? []),
      error: () => this.snackbarService.error('Der opstod en fejl ved genindlæsning af adgangsgrupper'),
    });
  }
}
